import {
	Box, Button,
	Flex,
	Icon, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Spinner,
	Table,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tr,
	useColorModeValue
} from '@chakra-ui/react';
import { useKeycloak } from '@react-keycloak/web';
import {
	createColumnHelper,
	flexRender,
	getCoreRowModel, getFilteredRowModel,
	getSortedRowModel,
	useReactTable
} from '@tanstack/react-table';
import Card from 'components/card/Card';
import { useEffect, useState } from "react";
import { MdOutlineDeleteOutline, MdOutlineEdit } from 'react-icons/md';
import { toast } from "react-toastify";
import { create, deleteModelType, getAll, update } from 'services/ModelTypeService';
import { CreateModelTypeRq, CreateModelTypeRs, GetModelTypeRs, UpdateModelTypeRq, UpdateModelTypeRs } from "../../../utils/interfaces";

const columnHelper = createColumnHelper<GetModelTypeRs>();

export default function DeadlinesTipologyConfigurations() {

	const { keycloak } = useKeycloak();

	// Models
	const [modelTypes, setModelTypes] = useState<GetModelTypeRs[]>([]);
	const [modelType, setModelType] = useState("");
	const [editId, setEditId] = useState(null);

	// Modal
	const [isAddModalOpen, setIsAddModalOpen] = useState(false);

	// API
	const [backendError, setBackendError] = useState(false);
	const [loading, setLoading] = useState(false);

	//Pagination
	const [currentPage, setCurrentPage] = useState(1);
	const [itemsPerPage] = useState(10);

	const startIndex = (currentPage - 1) * itemsPerPage;
	const endIndex = startIndex + itemsPerPage;
	const data = modelTypes?.slice(startIndex, endIndex);
	const totalPages = (modelTypes?.length === 0) ? 1 : Math.ceil(modelTypes?.length / itemsPerPage);

	const textColor = useColorModeValue('secondaryGray.900', 'white');

	useEffect(() => {
		fetchModelTypes()
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);


	useEffect(() => {
		if (backendError) {
			toast.error(
				"Errore durante la chiamata al Server, perfavore riprova.", {
				position: "bottom-right",
			});
		}
	}, [backendError]);

	const onSessionExpired = () => {
		toast.error("Sessione scaduta. Effettua nuovamente l'accesso.", {
			position: "bottom-right",
			onClose: () => {
				keycloak.logout();
				localStorage.removeItem("kc-token");
			}
		});
	};

	const columns = [
		columnHelper.accessor('id', {
			id: 'id',
			header: () => (
				<Text
					marginLeft='auto'
					marginRight='auto'
					justifyContent='center'
					align='center'
					fontSize={{ sm: '10px', lg: '14px' }}
					textTransform="none"
					color='gray.400'>
					#
				</Text>
			),
			cell: (info: any) => (
				<Flex align='center'>
					<Text color={textColor} fontSize='xs' fontWeight='700' marginLeft='auto' marginRight='auto'>
						{startIndex + info.row.index + 1}
					</Text>
				</Flex>
			)
		}),
		columnHelper.accessor('type', {
			id: 'type',
			header: () => (
				<Text
					marginLeft='auto'
					marginRight='auto'
					justifyContent='space-between'
					align='center'
					fontSize={{ sm: '10px', lg: '14px' }}
					textTransform="none"
					color='gray.400'>
					Tipo
				</Text>
			),
			cell: (info: any) => (
				<Flex align='center'>
					<Text color={textColor} fontSize='xs' fontWeight='700' marginLeft='auto' marginRight='auto'>
						{info.getValue()}
					</Text>
				</Flex>
			)
		}),
		{
			id: 'operation',
			header: () => (
				<Text
					marginLeft='auto'
					marginRight='auto'
					justifyContent='center'
					align='center'
					fontSize={{ sm: '10px', lg: '14px' }}
					textTransform="none"
					color='gray.400'>
					Azione
				</Text>
			),
			cell: (info: any) => (
				<Flex justifyContent='center'>
					<Button
						onClick={() => openEditModal(info.row.original.id, info.row.original.type)}
						_hover={{ bg: 'gray.200' }}
						_active={{ bg: 'gray.100' }}
						_focus={{ boxShadow: 'none' }}
						color="white"
						style={{
							height: '30px',
							padding: '0px 12px',
							borderRadius: '6px',
							border: 'none',
							cursor: 'pointer'
						}}
						fontSize='xs'
						mx="1"
					>
						<Icon as={MdOutlineEdit} w={4} h={4} color="black" />
					</Button>
					<Button
						onClick={() => deleteTipology(info.row.original.id)}
						_hover={{ bg: 'gray.200' }}
						_active={{ bg: 'gray.100' }}
						_focus={{ boxShadow: 'none' }}
						color="white"
						style={{
							height: '30px',
							padding: '0px 12px',
							borderRadius: '6px',
							border: 'none',
							cursor: 'pointer'
						}}
						fontSize='xs'
						mx="1"
					>
						<Icon as={MdOutlineDeleteOutline} w={4} h={4} color="black" />
					</Button>
				</Flex>
			)
		}
	];

	const table = useReactTable({
		columns,
		data,
		debugTable: false, // Disabilita i log di debug della tabella
		debugHeaders: false, // Disabilita i log di debug degli header
		debugColumns: false,  // Disabilita i log di debug delle colonne
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel()
	});

	const fetchModelTypes = async () => {
		try {
			const response = await getAll(keycloak.token, onSessionExpired);
			if (response) {
				setModelTypes(response);
			}
		} catch (error) {
			setBackendError(true);
		}
	}

	const nextPage = () => {
		setCurrentPage((prevPage) => Math.min(prevPage + 1, totalPages));
	};

	const prevPage = () => {
		setCurrentPage((prevPage) => Math.max(prevPage - 1, 1));
	};

	const closeAddModal = () => {
		setModelType("");
		setIsAddModalOpen(false);
	};

	const openEditModal = (id: string, type: string) => {
		setEditId(id);
		setModelType(type);
	};

	const closeEditModal = () => {
		setEditId(null);
		setModelType("");
	};

	const openAddModal = () => {
		setIsAddModalOpen(true);
	};

	const addModelTipology = async () => {
		try {
			setLoading(true);
			const modelTypeRq: CreateModelTypeRq = {
				type: modelType
			};

			const response: CreateModelTypeRs | null = await create(
				keycloak.token,
				onSessionExpired,
				modelTypeRq
			);
			if (response) {
				toast.success("Tipologia modello aggiunta con successo", { position: "bottom-right" });
				setModelTypes(await getAll(keycloak.token, onSessionExpired));
			} else {
				toast.error("Errore durante la creazione della tipologia modello", { position: "bottom-right" });
			}

			closeAddModal();
			setLoading(false);
		} catch (error) {
			setBackendError(true);
		}
	}

	const editTipology = async () => {
		try {
			setLoading(true);
			const modelTypeRq: UpdateModelTypeRq = {
				id: editId,
				type: modelType
			};

			const response: UpdateModelTypeRs | null = await update(
				keycloak.token,
				onSessionExpired,
				modelTypeRq,
			);
			if (response) {
				toast.success("Tipologia modello modificata con successo", { position: "bottom-right" });
				setModelTypes(await getAll(keycloak.token, onSessionExpired));
			} else {
				toast.error("Errore durante la modifica della tipologia modello", { position: "bottom-right" });
			}

			closeEditModal();
			setLoading(false);
		} catch (error) {
			setBackendError(true);
		}
	}

	const deleteTipology = async (id: any) => {
		try {
			setLoading(true);
			const response: boolean = await deleteModelType(keycloak.token, onSessionExpired, id);
			if (response) {
				toast.success("Tipologia modello eliminata con successo", { position: "bottom-right" });
				setModelTypes(await getAll(keycloak.token, onSessionExpired));
			} else {
				toast.error("Errore durante l'eliminazione della tipologia modello", { position: "bottom-right" });
			}

			setLoading(false);
		} catch (error) {
			setBackendError(true);
		}
	}

	return (
		<Box px='20px' mb="0px">
			{loading ? (
				<Flex justifyContent='center' alignItems='center' height='100vh'>
					<Spinner size='xl' />
				</Flex>
			) : (
				<Card>
					<Flex justifyContent='space-between' alignItems='center' mb={4}>
						<Text fontSize='xl' fontWeight='bold'>Tipologie Modelli</Text>
						<Button
							onClick={openAddModal}
							_hover={{ bg: 'gray.700' }}
							_active={{ bg: 'gray.600' }}
							_focus={{ boxShadow: 'none' }}
							backgroundColor='#688CC8'
							color="white"
							style={{
								height: '30px',
								padding: '0px 12px',
								borderRadius: '6px',
								border: 'none',
								cursor: 'pointer',
								marginLeft: 'auto'
							}}
							fontSize='xs'
							mx="1"
						>
							+ Aggiungi
						</Button>
					</Flex>
					<Table>
						<Thead>
							{table.getHeaderGroups().map(headerGroup => (
								<Tr key={headerGroup.id} p={1}>
									{headerGroup.headers.map(header => (
										<Th key={header.id} p={1}>
											{flexRender(header.column.columnDef.header, header.getContext())}
										</Th>
									))}
								</Tr>
							))}
						</Thead>
						<Tbody>
							{table.getRowModel().rows.map(row => (
								<Tr key={row.id} p={1}>
									{row.getVisibleCells().map(cell => (
										<Td key={cell.id} p={0.5}>
											{flexRender(cell.column.columnDef.cell, cell.getContext())}
										</Td>
									))}
								</Tr>
							))}
						</Tbody>
					</Table>
					<Flex justifyContent='center' alignItems='center' mt={4}>
						<Button fontSize={{ base: 'xs', md: 'md' }} onClick={prevPage} isDisabled={currentPage === 1}>
							Precedente
						</Button>
						<Text fontSize={{ base: 'xs', md: 'md' }}>{`Pagina ${currentPage} di ${totalPages}`}</Text>
						<Button fontSize={{ base: 'xs', md: 'md' }} onClick={nextPage} isDisabled={currentPage === totalPages}>
							Successiva
						</Button>
					</Flex>
				</Card>
			)}
			{/* Modal */}
			<Modal isOpen={!!editId} onClose={closeEditModal}>
				<ModalOverlay />
				<ModalContent>
					<ModalHeader color={"#9FB50C"}>Modifica Tipologia Modello</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<Input
							placeholder="Nuova tipologia modello..."
							value={modelType}
							onChange={(e) => setModelType(e.target.value)}
						/>
					</ModalBody>
					<ModalFooter>
						<Button color="white" background="#688CC8" mr={3} onClick={editTipology}>
							Salva
						</Button>
						<Button variant="ghost" onClick={closeEditModal}>
							Annulla
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
			<Modal isOpen={isAddModalOpen} onClose={closeAddModal}>
				<ModalOverlay />
				<ModalContent>
					<ModalHeader color={"#9FB50C"}>Nuova Tipologia Modello</ModalHeader>
					<ModalCloseButton _focus={{ boxShadow: 'none' }} />
					<ModalBody>
						<Input
							_focus={{ boxShadow: 'black' }}
							placeholder="Tipologia Modello..."
							value={modelType}
							onChange={(e) => setModelType(e.target.value)}
						/>
					</ModalBody>
					<ModalFooter>
						<Button
							colorScheme="gray"
							mr={3}
							onClick={() => addModelTipology()}
							color="white" background="#688CC8"
						>
							Salva
						</Button>
						<Button colorScheme="gray" onClick={closeAddModal}>Annulla</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</Box>
	);
}